import { useState, useEffect, useCallback } from "react"
import styles from "./hackathons-page.module.css"
import { getBrowserSupabaseClient } from "@/lib/supabase/client"
import { LoadingSkeleton } from "@/client/components/common/loading-skeleton"
import type { Hackathon } from "@/lib/supabase/types"

interface Team {
  id: number
  name: string
  members: number
  score: number
  rank: number
  submitted: boolean
}

const mockTeams: Team[] = [
  { id: 1, name: "Innovation Kings", members: 4, score: 92, rank: 1, submitted: true },
  { id: 2, name: "Code Warriors", members: 3, score: 88, rank: 2, submitted: true },
  { id: 3, name: "AI Pioneers", members: 5, score: 85, rank: 3, submitted: true },
  { id: 4, name: "Tech Dragons", members: 4, score: 78, rank: 4, submitted: false },
  { id: 5, name: "Cloud Nine", members: 3, score: 72, rank: 5, submitted: true },
]

//  HackathonStatus 映射到中文显示
const statusMap: Record<string, string> = {
  upcoming: "即将开始",
  registration: "报名中",
  ongoing: "进行中",
  judging: "评审中",
  completed: "已结束",
}

export default function HackathonsPage() {
  const [activeTab, setActiveTab] = useState<"all" | "upcoming" | "ongoing" | "finished">("all")
  const [searchQuery, setSearchQuery] = useState("")
  const [showModal, setShowModal] = useState(false)
  const [showTeamModal, setShowTeamModal] = useState(false)
  const [showScoreModal, setShowScoreModal] = useState(false)
  const [selectedHackathon, setSelectedHackathon] = useState<Hackathon | null>(null)
  const [editingHackathon, setEditingHackathon] = useState<Partial<Hackathon>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [hackathons, setHackathons] = useState<Hackathon[]>([])
  const [stats, setStats] = useState({
    total: 0,
    upcoming: 0,
    ongoing: 0,
    finished: 0,
    totalParticipants: 0,
  })

  // 获取 hackathons 数据
  const fetchHackathons = useCallback(async () => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from("hackathons")
        .select("*")
        .order("created_at", { ascending: false })

      if (error) throw error

      const hackathonData = data || []
      setHackathons(hackathonData)

      // 计算统计数据
      const now = new Date()
      const upcoming = hackathonData.filter(
        (h) => h.status === "upcoming" || h.status === "registration"
      ).length
      const ongoing = hackathonData.filter((h) => h.status === "ongoing" || h.status === "judging").length
      const finished = hackathonData.filter((h) => h.status === "completed").length
      const totalParticipants = hackathonData.reduce((sum, h) => sum + (h.current_participants || 0), 0)

      setStats({
        total: hackathonData.length,
        upcoming,
        ongoing,
        finished,
        totalParticipants,
      })
    } catch (error) {
      console.error("获取赛事数据失败:", error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchHackathons()
  }, [fetchHackathons])

  // 过滤赛事
  const filteredHackathons = hackathons.filter((hackathon) => {
    const matchesTab =
      activeTab === "all" ||
      (activeTab === "upcoming" &&
        (hackathon.status === "upcoming" || hackathon.status === "registration")) ||
      (activeTab === "ongoing" &&
        (hackathon.status === "ongoing" || hackathon.status === "judging")) ||
      (activeTab === "finished" && hackathon.status === "completed")
    const matchesSearch = hackathon.title.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesTab && matchesSearch
  })

  // 创建赛事
  const handleCreateHackathon = () => {
    setEditingHackathon({})
    setShowModal(true)
  }

  // 编辑赛事
  const handleEditHackathon = (hackathon: Hackathon) => {
    setEditingHackathon({ ...hackathon })
    setShowModal(true)
  }

  // 保存赛事
  const handleSaveHackathon = async () => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    try {
      const now = new Date().toISOString()
      const hackathonData = {
        ...editingHackathon,
        updated_at: now,
      } as Partial<Hackathon>

      if (editingHackathon.id) {
        // 更新现有赛事
        const { error } = await supabase
          .from("hackathons")
          .update(hackathonData)
          .eq("id", editingHackathon.id)
        if (error) throw error
      } else {
        // 创建新赛事
        const { error } = await supabase.from("hackathons").insert({
          ...hackathonData,
          created_at: now,
        })
        if (error) throw error
      }

      setShowModal(false)
      setEditingHackathon({})
      fetchHackathons()
    } catch (error) {
      console.error("保存赛事失败:", error)
      alert("保存失败，请重试")
    }
  }

  // 查看队伍
  const handleViewTeams = (hackathon: Hackathon) => {
    setSelectedHackathon(hackathon)
    setShowTeamModal(true)
  }

  // 删除赛事
  const handleDeleteHackathon = async (id: string) => {
    if (!confirm("确定要删除这个赛事吗？此操作不可恢复。")) return

    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    try {
      const { error } = await supabase.from("hackathons").delete().eq("id", id)
      if (error) throw error
      fetchHackathons()
    } catch (error) {
      console.error("删除赛事失败:", error)
      alert("删除失败，请重试")
    }
  }

  // 格式化日期显示
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("zh-CN")
  }

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>赛事管理</h1>
        </div>
        <div className={styles.stats}>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={styles.statItem}>
              <LoadingSkeleton variant="text" />
            </div>
          ))}
        </div>
        <div className={styles.searchBar}>
          <LoadingSkeleton variant="text" />
        </div>
        <div className={styles.hackathonList}>
          {[1, 2, 3].map((i) => (
            <LoadingSkeleton key={i} variant="rectangular" height={150} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>赛事管理</h1>
        <button className={styles.addBtn} onClick={handleCreateHackathon}>
          + 创建赛事
        </button>
      </div>

      {/*  统计卡片  */}
      <div className={styles.stats}>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{stats.total}</span>
          <span className={styles.statLabel}>总赛事数</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{stats.upcoming}</span>
          <span className={styles.statLabel}>报名中</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{stats.ongoing}</span>
          <span className={styles.statLabel}>进行中</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{stats.finished}</span>
          <span className={styles.statLabel}>已完成</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{stats.totalParticipants}</span>
          <span className={styles.statLabel}>总参赛人数</span>
        </div>
      </div>

      {/*  搜索栏  */}
      <div className={styles.searchBar}>
        <input
          type="text"
          placeholder="搜索赛事名称..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
        />
        <select className={styles.filterSelect}>
          <option value="">全部状态</option>
          <option value="upcoming">即将开始</option>
          <option value="registration">报名中</option>
          <option value="ongoing">进行中</option>
          <option value="judging">评审中</option>
          <option value="completed">已结束</option>
        </select>
      </div>

      {/*  标签页  */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === "all" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("all")}
        >
          全部
        </button>
        <button
          className={`${styles.tab} ${activeTab === "upcoming" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("upcoming")}
        >
          报名中
        </button>
        <button
          className={`${styles.tab} ${activeTab === "ongoing" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("ongoing")}
        >
          进行中
        </button>
        <button
          className={`${styles.tab} ${activeTab === "finished" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("finished")}
        >
          已结束
        </button>
      </div>

      {/*  赛事列表  */}
      <div className={styles.hackathonList}>
        {filteredHackathons.length === 0 ? (
          <div className={styles.emptyState}>暂无赛事数据</div>
        ) : (
          filteredHackathons.map((hackathon) => (
            <div key={hackathon.id} className={styles.hackathonCard}>
              <div className={styles.banner}>
                {hackathon.cover_url ? (
                  <img src={hackathon.cover_url} alt={hackathon.title} className={styles.bannerImage} />
                ) : (
                  <span className={styles.bannerIcon}>🏆</span>
                )}
              </div>
              <div className={styles.info}>
                <h3>{hackathon.title}</h3>
                <p className={styles.description}>
                  {hackathon.description || "暂无描述"}
                </p>
                <div className={styles.meta}>
                  <span>
                    📅 {formatDate(hackathon.event_start)} ~ {formatDate(hackathon.event_end)}
                  </span>
                  <span>💰 {(hackathon.prize_pool_bbp / 100).toLocaleString()} BBP</span>
                </div>
                <div className={styles.stats2}>
                  <span>{hackathon.current_participants || 0} 参赛者</span>
                  <span>{hackathon.max_participants || "无限制"} 名额上限</span>
                </div>
                <span className={`${styles.status} ${styles[hackathon.status]}`}>
                  {statusMap[hackathon.status] || hackathon.status}
                </span>
              </div>
              <div className={styles.actions}>
                <button className={styles.viewBtn} onClick={() => handleViewTeams(hackathon)}>
                  队伍
                </button>
                <button
                  className={styles.viewBtn}
                  onClick={() => {
                    setSelectedHackathon(hackathon)
                    setShowScoreModal(true)
                  }}
                >
                  评分
                </button>
                <button className={styles.editBtn} onClick={() => handleEditHackathon(hackathon)}>
                  编辑
                </button>
                <button
                  className={styles.deleteBtn}
                  onClick={() => handleDeleteHackathon(hackathon.id)}
                >
                  删除
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/*  创建/编辑赛事弹窗  */}
      {showModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>{editingHackathon.id ? "编辑赛事" : "创建赛事"}</h2>
              <button className={styles.closeBtn} onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>赛事名称</label>
                <input
                  type="text"
                  placeholder="输入赛事名称"
                  value={editingHackathon.title || ""}
                  onChange={(e) => setEditingHackathon({ ...editingHackathon, title: e.target.value })}
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label>赛事描述</label>
                <textarea
                  placeholder="输入赛事描述..."
                  value={editingHackathon.description || ""}
                  onChange={(e) =>
                    setEditingHackathon({ ...editingHackathon, description: e.target.value })
                  }
                  className={styles.textarea}
                  rows={3}
                />
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>报名开始</label>
                  <input
                    type="date"
                    value={
                      editingHackathon.registration_start
                        ? editingHackathon.registration_start.split("T")[0]
                        : ""
                    }
                    onChange={(e) =>
                      setEditingHackathon({
                        ...editingHackathon,
                        registration_start: e.target.value,
                      })
                    }
                    className={styles.input}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>报名结束</label>
                  <input
                    type="date"
                    value={
                      editingHackathon.registration_end
                        ? editingHackathon.registration_end.split("T")[0]
                        : ""
                    }
                    onChange={(e) =>
                      setEditingHackathon({
                        ...editingHackathon,
                        registration_end: e.target.value,
                      })
                    }
                    className={styles.input}
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>赛事开始</label>
                  <input
                    type="date"
                    value={
                      editingHackathon.event_start
                        ? editingHackathon.event_start.split("T")[0]
                        : ""
                    }
                    onChange={(e) =>
                      setEditingHackathon({ ...editingHackathon, event_start: e.target.value })
                    }
                    className={styles.input}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>赛事结束</label>
                  <input
                    type="date"
                    value={
                      editingHackathon.event_end ? editingHackathon.event_end.split("T")[0] : ""
                    }
                    onChange={(e) =>
                      setEditingHackathon({ ...editingHackathon, event_end: e.target.value })
                    }
                    className={styles.input}
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>奖金池 (BBP)</label>
                  <input
                    type="number"
                    placeholder="50000"
                    value={editingHackathon.prize_pool_bbp || ""}
                    onChange={(e) =>
                      setEditingHackathon({
                        ...editingHackathon,
                        prize_pool_bbp: Number(e.target.value),
                      })
                    }
                    className={styles.input}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>参赛名额上限</label>
                  <input
                    type="number"
                    placeholder="无限制"
                    value={editingHackathon.max_participants || ""}
                    onChange={(e) =>
                      setEditingHackathon({
                        ...editingHackathon,
                        max_participants: e.target.value ? Number(e.target.value) : null,
                      })
                    }
                    className={styles.input}
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>状态</label>
                <select
                  className={styles.select}
                  value={editingHackathon.status || "upcoming"}
                  onChange={(e) =>
                    setEditingHackathon({
                      ...editingHackathon,
                      status: e.target.value as Hackathon["status"],
                    })
                  }
                >
                  <option value="upcoming">即将开始</option>
                  <option value="registration">报名中</option>
                  <option value="ongoing">进行中</option>
                  <option value="judging">评审中</option>
                  <option value="completed">已结束</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>赛事封面 URL</label>
                <input
                  type="text"
                  placeholder="https://example.com/cover.jpg"
                  value={editingHackathon.cover_url || ""}
                  onChange={(e) =>
                    setEditingHackathon({ ...editingHackathon, cover_url: e.target.value })
                  }
                  className={styles.input}
                />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setShowModal(false)}>
                取消
              </button>
              <button className={styles.submitBtn} onClick={handleSaveHackathon}>
                {editingHackathon.id ? "保存" : "创建"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/*  队伍管理弹窗  */}
      {showTeamModal && selectedHackathon && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalLarge}>
            <div className={styles.modalHeader}>
              <h2>{selectedHackathon.title} - 队伍管理</h2>
              <button className={styles.closeBtn} onClick={() => setShowTeamModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.teamStats}>
              <div className={styles.teamStatItem}>
                <span className={styles.teamStatValue}>{mockTeams.length}</span>
                <span className={styles.teamStatLabel}>参赛队伍</span>
              </div>
              <div className={styles.teamStatItem}>
                <span className={styles.teamStatValue}>
                  {selectedHackathon.current_participants || 0}
                </span>
                <span className={styles.teamStatLabel}>参赛人数</span>
              </div>
              <div className={styles.teamStatItem}>
                <span className={styles.teamStatValue}>
                  {mockTeams.filter((t) => t.submitted).length}
                </span>
                <span className={styles.teamStatLabel}>已提交</span>
              </div>
            </div>
            <div className={styles.teamList}>
              {mockTeams.map((team) => (
                <div key={team.id} className={styles.teamItem}>
                  <div className={styles.teamRank}>#{team.rank}</div>
                  <div className={styles.teamInfo}>
                    <h4>{team.name}</h4>
                    <span className={styles.teamMeta}>
                      {team.members} 人 · {team.submitted ? "✅ 已提交" : "⏳ 未提交"}
                    </span>
                  </div>
                  <div className={styles.teamScore}>
                    <span className={styles.scoreLabel}>当前得分</span>
                    <span className={styles.scoreValue}>{team.score}</span>
                  </div>
                  <div className={styles.teamActions}>
                    <button className={styles.smallBtn}>详情</button>
                    <button className={styles.smallBtn}>评分</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/*  评分系统弹窗  */}
      {showScoreModal && selectedHackathon && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>{selectedHackathon.title} - 评分系统</h2>
              <button className={styles.closeBtn} onClick={() => setShowScoreModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>评分维度</label>
                <div className={styles.criteriaList}>
                  <div className={styles.criteriaItem}>
                    <span className={styles.criteriaName}>创新性</span>
                    <div className={styles.criteriaActions}>
                      <input type="number" min="0" max="25" defaultValue="25" className={styles.scoreInput} />
                      <span>分</span>
                    </div>
                  </div>
                  <div className={styles.criteriaItem}>
                    <span className={styles.criteriaName}>技术实现</span>
                    <div className={styles.criteriaActions}>
                      <input type="number" min="0" max="25" defaultValue="25" className={styles.scoreInput} />
                      <span>分</span>
                    </div>
                  </div>
                  <div className={styles.criteriaItem}>
                    <span className={styles.criteriaName}>商业价值</span>
                    <div className={styles.criteriaActions}>
                      <input type="number" min="0" max="25" defaultValue="25" className={styles.scoreInput} />
                      <span>分</span>
                    </div>
                  </div>
                  <div className={styles.criteriaItem}>
                    <span className={styles.criteriaName}>用户体验</span>
                    <div className={styles.criteriaActions}>
                      <input type="number" min="0" max="25" defaultValue="25" className={styles.scoreInput} />
                      <span>分</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>评分截止时间</label>
                <input type="datetime-local" className={styles.input} defaultValue="2024-06-15T18:00" />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input type="checkbox" className={styles.checkbox} defaultChecked />
                  启用 AI 辅助评分
                </label>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setShowScoreModal(false)}>
                取消
              </button>
              <button className={styles.submitBtn} onClick={() => setShowScoreModal(false)}>
                保存设置
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
